import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Input,
  Modal,
  Form,
  message,
  ConfigProvider,
  Popconfirm,
  Space,
  Select,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from "@ant-design/icons";
import "../adminShared/adminPage.css";
import { routes } from "../../api/routes";
import { callApi } from "../../api/apiCaller";
import Loader from "../../components/loader/loader";
import { listFromResponse, isMutationSuccess } from "../../helper/helper";
import dayjs from "dayjs";

const VehicleVariants = () => {
  const [allRows, setAllRows] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [form] = Form.useForm();

  const loadModels = () => {
    callApi(
      "GET",
      routes.getAllVehicleModels,
      null,
      setLoading,
      (res) => {
        if (res.status === 200) setModels(listFromResponse(res));
      },
      () => {}
    );
  };

  const loadVariants = () => {
    callApi(
      "GET",
      routes.getAllVehicleVariants,
      null,
      setLoading,
      (res) => {
        if (res.status === 200) {
          const data = listFromResponse(res);
          setAllRows(data);
        }
      },
      () => message.error("Failed to load variants")
    );
  };

  useEffect(() => {
    loadModels();
    loadVariants();
  }, []);

  const getModelLabel = (row) => {
    const m = row.vehicleModel;
    if (typeof m === "object" && m?.title) return m.title;
    const id = typeof m === "string" ? m : m?._id;
    const found = models.find((x) => x._id === id);
    return found?.title || id || "—";
  };

  const filtered = searchText
    ? allRows.filter((r) => (r.title || "").toLowerCase().includes(searchText.toLowerCase()))
    : allRows;

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    const modelId =
      typeof row.vehicleModel === "object" ? row.vehicleModel?._id : row.vehicleModel;
    form.setFieldsValue({ title: row.title, vehicleModel: modelId });
    setModalOpen(true);
  };

  const submit = (values) => {
    setModalOpen(false);
    const current = editing;
    setEditing(null);
    form.resetFields();

    if (current) {
      callApi(
        "PATCH",
        `${routes.updateVehicleVariant}/${current._id}`,
        { title: values.title, vehicleModel: values.vehicleModel },
        setLoading,
        (res) => {
          if (res.status === 200) {
            message.success("Variant updated");
            loadVariants();
          }
        },
        () => message.error("Failed to update variant")
      );
    } else {
      callApi(
        "POST",
        routes.createVehicleVariant,
        { title: values.title, vehicleModel: values.vehicleModel },
        setLoading,
        (res) => {
          if (res.status === 200 || res.status === 201) {
            message.success("Variant created");
            loadVariants();
          }
        },
        () => message.error("Failed to create variant")
      );
    }
  };

  const remove = (id) => {
    callApi(
      "DELETE",
      `${routes.deleteVehicleVariant}/${id}`,
      null,
      setLoading,
      (res) => {
        if (isMutationSuccess(res)) {
          message.success("Variant deleted");
          loadVariants();
        }
      },
      () => message.error("Failed to delete variant")
    );
  };

  const columns = [
    { title: "No", key: "i", width: 60, render: (_, __, i) => i + 1 },
    { title: "Title", dataIndex: "title", key: "title" },
    {
      title: "Model",
      key: "model",
      render: (_, row) => getModelLabel(row),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 140,
      render: (d) => (d ? dayjs(d).format("DD MMM YYYY") : "—"),
    },
    {
      title: "Action",
      key: "a",
      width: 160,
      render: (_, row) => (
        <Space>
          <Button type="primary" size="small" icon={<EditOutlined />} onClick={() => openEdit(row)} />
          <Popconfirm title="Delete this variant?" onConfirm={() => remove(row._id)}>
            <Button danger size="small" icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <ConfigProvider theme={{ token: { colorPrimary: "#555BDF" } }}>
      <div className="admin-entity-page">
        <Loader loading={loading} />
        <div className="admin-entity-header">
          <h1 className="admin-entity-title">Vehicle Variants</h1>
        </div>
        <div className="admin-entity-card">
          <div className="admin-entity-actions">
            <h2 style={{ margin: 0, fontSize: 16 }}>Catalog</h2>
            <Space>
              <Input
                placeholder="Search…"
                prefix={<SearchOutlined />}
                allowClear
                style={{ width: 260 }}
                onChange={(e) => setSearchText(e.target.value || "")}
              />
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                Add variant
              </Button>
            </Space>
          </div>
          <Table
            className="admin-entity-table"
            rowKey="_id"
            columns={columns}
            dataSource={filtered}
            scroll={{ x: true }}
          />
        </div>
        <Modal
          title={editing ? "Edit variant" : "New variant"}
          open={modalOpen}
          onCancel={() => {
            setModalOpen(false);
            setEditing(null);
            form.resetFields();
          }}
          onOk={() => form.submit()}
          destroyOnClose
        >
          <Form form={form} layout="vertical" onFinish={submit}>
            <Form.Item
              name="vehicleModel"
              label="Model"
              rules={[{ required: true, message: "Required" }]}
            >
              <Select
                placeholder="Select model"
                options={models.map((m) => ({ value: m._id, label: m.title }))}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
            <Form.Item name="title" label="Title" rules={[{ required: true, message: "Required" }]}>
              <Input placeholder="e.g. Sportback S Line 35 TFSI" />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </ConfigProvider>
  );
};

export default VehicleVariants;
